export const GEOCODE_VERSION = 3;

const normalizeValue = (value) => (value || '').toString().trim();

const COUNTRY_ALIASES = {
    de: { normalized: 'germany', code: 'DE' },
    deutschland: { normalized: 'germany', code: 'DE' },
    germany: { normalized: 'germany', code: 'DE' },
    mx: { normalized: 'mexico', code: 'MX' },
    mexiko: { normalized: 'mexico', code: 'MX' },
    mexico: { normalized: 'mexico', code: 'MX' },
};

const normalizeCountry = (country) => {
    const normalizedValue = normalizeValue(country).toLowerCase();
    return COUNTRY_ALIASES[normalizedValue]?.normalized || normalizedValue;
};

const getCountryCode = (country) => {
    const normalizedValue = normalizeValue(country).toLowerCase();
    return COUNTRY_ALIASES[normalizedValue]?.code || null;
};

const buildAddressCandidate = (...parts) =>
    parts
        .map(normalizeValue)
        .filter(Boolean)
        .join(', ');

const buildCandidateList = (address) => {
    const title = normalizeValue(address.title);
    const street = normalizeValue(address.street);
    const zip = normalizeValue(address.zip);
    const city = normalizeValue(address.city);
    const country = normalizeValue(address.country);

    const cityName = city.split(',')[0]?.trim() || city;
    const cityRegion = city.includes(',') ? city : '';

    const candidates = [
        buildAddressCandidate(street, zip, city, country),
        buildAddressCandidate(title, street, zip, city, country),
        buildAddressCandidate(street, cityRegion, zip, cityName, country),
        buildAddressCandidate(title, street, cityRegion, zip, cityName, country),
        buildAddressCandidate(street, zip, city),
        buildAddressCandidate(title, street, zip, city),
        buildAddressCandidate(street, cityRegion, zip, cityName),
        buildAddressCandidate(title, street, cityRegion, zip, cityName),
        buildAddressCandidate(street, city, country),
        buildAddressCandidate(title, city, country),
        buildAddressCandidate(street, city),
        buildAddressCandidate(title, city),
    ];

    return [...new Set(candidates.filter(Boolean))];
};

const geocodeCandidate = (geocoder, candidate, country) =>
    new Promise((resolve) => {
        const request = { address: candidate };
        const countryCode = getCountryCode(country);

        if (countryCode) {
            request.componentRestrictions = { country: countryCode };
        }

        geocoder.geocode(request, (results, status) => {
            resolve({ results, status, candidate });
        });
    });

const wait = (delayMs) =>
    new Promise((resolve) => {
        setTimeout(resolve, delayMs);
    });

const geocodeCandidateWithRetry = async (geocoder, candidate, country) => {
    const retryableStatuses = new Set(['OVER_QUERY_LIMIT', 'UNKNOWN_ERROR', 'ERROR']);
    let attempt = 0;

    while (attempt < 4) {
        const response = await geocodeCandidate(geocoder, candidate, country);

        if (!retryableStatuses.has(response.status)) {
            return response;
        }

        attempt += 1;

        if (attempt < 4) {
            await wait(300 * attempt);
        }
    }

    return geocodeCandidate(geocoder, candidate, country);
};

export const shouldRefreshGeocode = (address) => {
    if (!address.position) {
        return true;
    }

    const normalizedCountry = normalizeCountry(address.country);
    const countriesNeedingRefresh = new Set(['germany', 'mexico']);

    return countriesNeedingRefresh.has(normalizedCountry) && address.geocodeVersion !== GEOCODE_VERSION;
};

export const geocodeStoredAddress = async (geocoder, address) => {
    const candidates = buildCandidateList(address);
    let lastStatus = 'ZERO_RESULTS';

    for (const candidate of candidates) {
        const { results, status } = await geocodeCandidateWithRetry(geocoder, candidate, address.country);

        if (status === 'OK' && results?.[0]?.geometry?.location) {
            return {
                position: {
                    lat: results[0].geometry.location.lat(),
                    lng: results[0].geometry.location.lng(),
                },
                geocodeQuery: candidate,
                geocodeVersion: GEOCODE_VERSION,
            };
        }

        lastStatus = status;
    }

    console.warn('Geocode failed', {
        title: address.title,
        street: address.street,
        zip: address.zip,
        city: address.city,
        country: address.country,
        lastStatus,
        candidates,
    });

    return null;
};
